import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import EOEPreviewPopup from '../EOEPreviewPopup';
import { getDraftBillingCycles } from '../../../services/api';
import type { GetBillingCyclesResponse } from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { useBillingCycles } from '../../../hooks/useBillingCycles';

interface EOEBillingNotReadyEntryProps {
  entry: GetBillingCyclesResponse;
  index?: number;
  onPreview: (entry: GetBillingCyclesResponse) => void;
}

type ReadinessStatus = 'pass' | 'fail' | 'na';

interface ReadinessItem {
  label: string;
  status: ReadinessStatus;
}

function getReadinessItems(entry: GetBillingCyclesResponse): ReadinessItem[] {
  const medicareStatus: ReadinessStatus = !entry.is_medicare_number_required
    ? 'na'
    : entry.is_medicare_number_valid
      ? 'pass'
      : 'fail';

  const pcrStatus: ReadinessStatus = !entry.is_pcr_tracking_number_required
    ? 'na'
    : entry.is_pcr_tracking_number_valid
      ? 'pass'
      : 'fail';

  return [
    { label: 'OASIS', status: entry.all_oasis_correct_state ? 'pass' : 'fail' },
    { label: 'HIPPS', status: entry.hipps_code !== '' ? 'pass' : 'fail' },
    { label: 'Visits', status: entry.all_visits_completed ? 'pass' : 'fail' },
    { label: 'Orders', status: entry.is_f2f_valid ? 'pass' : 'fail' },
    { label: 'Medicare #', status: medicareStatus },
    { label: 'PCR Tracking #', status: pcrStatus },
  ];
}

function isClaimReady(items: ReadinessItem[]): boolean {
  return items.every(item => item.status !== 'fail');
}

const ReadinessIcon: React.FC<{ status: ReadinessStatus }> = ({ status }) => {
  if (status === 'na') {
    return <span className='text-muted-foreground text-xs'>N/A</span>;
  }
  if (status === 'pass') {
    return <Check className='h-4 w-4 text-green-600' />;
  }
  return <X className='h-4 w-4 text-red-600' />;
};

const EOEBillingNotReadyEntry: React.FC<EOEBillingNotReadyEntryProps> = ({
  entry,
  index,
  onPreview,
}) => {
  const startDate = formatDateToMMDDYYYY(entry.start_date);
  const endDate = formatDateToMMDDYYYY(entry.end_date);
  const readinessItems = getReadinessItems(entry);
  const ready = isClaimReady(readinessItems);
  const isAlternate = index !== undefined && index % 2 === 1;

  return (
    <TableRow
      className={
        isAlternate
          ? 'bg-muted hover:bg-muted'
          : 'bg-background hover:bg-background'
      }
    >
      <TableCell>{entry.patient_name}</TableCell>
      <TableCell>{entry.patient_mrn}</TableCell>
      <TableCell>{entry.clinic_name}</TableCell>
      <TableCell>{entry.insurance_name}</TableCell>
      <TableCell>
        <span>{startDate} -</span>
        <br />
        <span>{endDate}</span>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {ready ? (
                <div className='flex items-center justify-center cursor-default w-fit'>
                  <Check className='h-4 w-4 text-green-600' />
                </div>
              ) : (
                <button
                  type='button'
                  onClick={() => onPreview(entry)}
                  className='flex items-center justify-center cursor-pointer w-fit rounded-sm outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring'
                  aria-label='Open claim preview'
                >
                  <X className='h-4 w-4 text-red-600' />
                </button>
              )}
            </TooltipTrigger>
            <TooltipContent side='right' className='p-3'>
              <ul className='space-y-1.5'>
                {readinessItems.map(item => (
                  <li
                    key={item.label}
                    className='flex items-center justify-between gap-4'
                  >
                    <span className='text-xs'>{item.label}</span>
                    <ReadinessIcon status={item.status} />
                  </li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
};

const EOENotReadyTab: React.FC = () => {
  const [draftPopupCycle, setDraftPopupCycle] =
    useState<GetBillingCyclesResponse | null>(null);

  const { data, isLoading } = useBillingCycles<
    GetBillingCyclesResponse,
    { ready_for_review: boolean }
  >({
    fetchFn: getDraftBillingCycles,
    fetchPayload: { ready_for_review: false },
    errorMessage: 'Could not fetch data for the "Not Ready" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
  });

  if (isLoading) {
    return (
      <p className='text-muted-foreground text-center py-10'>Loading...</p>
    );
  }

  if (data.length === 0) {
    return (
      <p className='text-muted-foreground text-center py-10'>
        There are currently no records to display
      </p>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Patient Name</TableHead>
            <TableHead>MRN</TableHead>
            <TableHead>Clinic</TableHead>
            <TableHead>Insurance</TableHead>
            <TableHead>Billing Period</TableHead>
            <TableHead>Claim Readiness</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((entry, index) => (
            <EOEBillingNotReadyEntry
              key={entry.billing_cycle_id}
              entry={entry}
              index={index}
              onPreview={setDraftPopupCycle}
            />
          ))}
        </TableBody>
      </Table>
      <EOEPreviewPopup
        billingCycle={draftPopupCycle}
        onOpenChange={open => !open && setDraftPopupCycle(null)}
      />
    </>
  );
};

export default EOENotReadyTab;
